import React, {useRef} from 'react';
import {API} from "aws-amplify";
import {toast} from "react-toastify";
import _ from "lodash";

import Modal from "@/common/Modal";
import moment from '@/common/momentConfig';
import {getSurveyOriginationMethodValue, SurveyTimestamps} from "@/common/SurveyUtils";
import {canQASurveys, isPrintSurveyEnabled} from "@/shared/access";

import {
    SurveyResultsAnswerChoice,
    SurveyResultsAnswerScale,
    SurveyResultsAnswerComment
} from "../survey/SurveyResultsAnswerCell"
import SurveyResultsDetailsAttribute from "../survey/SurveyResultsDetailsAttribute"
import {MessageCellBot, MessageCellIncoming} from "../conversations/MessageCell";
import AddTask from "../tasks/AddTask";
import EncounterDetails from "../encounter/EncounterDetails";
import {updateExcludeComment} from "../../shared/survey/actions";
import {useDispatch} from "react-redux";
import IgnoreAnswerModal from "./IgnoreAnswerModal";

function SurveyResults({surveyInstanceId, onChange, opened, setAutoOpenModal, onModalClose, ...props}) {
    const [open, setOpen] = React.useState(false);
    const [activeTab, setActiveTab] = React.useState("RESULTS");
    const [surveyInstance, setSurveyInstance] = React.useState(null);
    const [messages, setMessages] = React.useState([]);
    const [taskTypes, setTaskTypes] = React.useState([]);
    const [openIgnoreModal, setOpenIgnoreModal] = React.useState(false);
    const [answerIgnoreModal, setAnswerIgnoreModal] = React.useState(null);
    const [questionIgnoreModal, setQuestionIgnoreModal] = React.useState(null);
    const dispatch = useDispatch();

    React.useEffect(() => {
        if (open) loadData();
        else if (opened) setOpen(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const loadData = () => {
        setSurveyInstance(null);
        API.get('Core', '/api/v1/survey/instance/' + surveyInstanceId)
            .then(response => {
                    setSurveyInstance(Object.assign({}, response));
                },
                error => {
                    toast("Could not load survey results: " + error.response.data.message, {
                        position: "top-center",
                        type: "error"
                    });
                }
            );

        setMessages([]);
        API.get('Core', '/api/v1/survey/instance/' + surveyInstanceId + '/messages')
            .then(response => {
                    setMessages(Object.assign([], response));
                },
                error => {
                    toast("Could not load survey transcript: " + error.response.data.message, {
                        position: "top-center",
                        type: "error"
                    });
                }
            );

        if (taskTypes == null || taskTypes.length === 0) {
            setTaskTypes([]);
            API.get('Core', '/api/v1/task/types')
                .then(response => {
                        setTaskTypes(Object.assign([], response));
                    },
                    error => {
                    }
                );
        }
    }

    const clearReviewFlag = () => {
        surveyInstance.surveyInstance.review = false;
        API.put('Core', '/api/v1/survey/instance/' + surveyInstanceId, {body: surveyInstance.surveyInstance})
            .then(response => {
                    setSurveyInstance(response);
                },
                error => {
                    toast("Could not update survey instance: " + error.response.data.message, {
                        position: "top-center",
                        type: "error"
                    });
                }
            );
    }

    const addReviewFlag = () => {
        surveyInstance.surveyInstance.review = true;
        API.put('Core', '/api/v1/survey/instance/' + surveyInstanceId, {body: surveyInstance.surveyInstance})
            .then(response => {
                    setSurveyInstance(response);
                },
                error => {
                    toast("Could not update survey instance: " + error.response.data.message, {
                        position: "top-center",
                        type: "error"
                    });
                }
            );
    }

    const closeModal = () => {
        if (onModalClose) {
            onModalClose();
        }
        if (onChange) {
            onChange(surveyInstance);
        }
        setOpen(false);
        if (setAutoOpenModal && typeof setAutoOpenModal === 'function') setAutoOpenModal(false);
    }

    const onAddTaskSubmit = (newTask) => {
        newTask.sourceSurvey = surveyInstance.surveyInstance;
        newTask.sourceEncounter = surveyInstance.ruleTriggerInstance != null ? surveyInstance.ruleTriggerInstance.encounter : null;
        API.post('Core', '/api/v1/task', {body: newTask})
            .then(response => {
                    toast("Task created!", {position: "top-center", type: "success"});
                },
                error => {
                    toast("Task creation failure!", {position: "top-center", type: "error"});
                }
            );
    }


    const getTabTitle = (tabKey) => {
        // eslint-disable-next-line default-case
        switch (tabKey) {
            case "RESULTS":
                return "Results";
            case "MESSAGES":
                return "Messages";
        }
    }

    const tabs = [
        "RESULTS",
        "MESSAGES"
    ]

    const showIgnoreModal = (answer, question) => {
        setAnswerIgnoreModal(answer);
        setQuestionIgnoreModal(question);
        setOpenIgnoreModal(true);
    }

    const updateAnswerIgnored = (answer, {ignored, ignoredComment}) => {
        API.put("Core", `/api/v1/survey/answer/ignore/${answer.id}`, {
            body: {
                ignored,
                ignoredComment,
            },
        }).then(
            (response) => {
                mutateAnswer(answer, {ignored: response.ignored, ignoredComment: response.ignoredComment});
                toast(`Answer ${ignored ? "marked" : "unmarked"} as ignored`, {
                    position: "top-center",
                    type: "success",
                });
            },
            (error) => {
                toast("Answer update failure", {
                    position: "top-center",
                    type: "error",
                });
            }
        );
    }

    const setAnswerIgnored = (answer, ignored, question) => {
        if (ignored === true) {
            showIgnoreModal(answer, question);
            return;
        }

        updateAnswerIgnored(answer, {ignored, ignoredComment: answer.ignoredComment});
    }

    const mutateAnswer = (answer, props) => {
        const idx = surveyInstance.surveyInstance.answers.findIndex(a => a.id === answer.id);
        setSurveyInstance((state) => ({
            ...state,
            surveyInstance: {
                ...state.surveyInstance,
                answers: [
                    ...state.surveyInstance.answers.slice(0, idx),
                    {
                        ...state.surveyInstance.answers[idx],
                        ...props
                    },
                    ...state.surveyInstance.answers.slice(idx + 1)
                ]
            }
        }))
    }
    const renderQuestion = (question, answer) => {
        switch (question.type) {
            case "COMMENT":
                return <SurveyResultsAnswerComment key={question.id} question={question} answer={answer}
                                                   setAnswerIgnored={setAnswerIgnored}/>;
            case "CHOICE":
                return <SurveyResultsAnswerChoice key={question.id} question={question} answer={answer}
                                                  setAnswerIgnored={setAnswerIgnored}/>;
            case "SCALE":
                return <SurveyResultsAnswerScale key={question.id} question={question} answer={answer}
                                                 setAnswerIgnored={setAnswerIgnored}/>;
            default:
                return null;
        }
    };

    const getTabContents = (tabKey) => {
        if (!surveyInstance) {
            return null;
        }
        switch (tabKey) {
            case "RESULTS":
                return (
                    <div className="contentView">
                        {_.map(_.sortBy(surveyInstance.surveyVersion.questions, qu => qu.questionOrder), question => {
                            let answer = _.find(surveyInstance.surveyInstance.answers, a => question.id === a.question.id);
                            let result = [renderQuestion(question, answer)];
                            if (question.surveyQuestionReviewCriteria) {
                                question.surveyQuestionReviewCriteria.forEach(criteria => {
                                    if (criteria.followUpQuestion) {
                                        let followUpAnswer = _.find(surveyInstance.surveyInstance.answers, a => criteria.followUpQuestion.id === a.question.id);
                                        if (followUpAnswer) result.push(renderQuestion(criteria.followUpQuestion, followUpAnswer));
                                    }
                                });
                            }
                            return result;
                        })}
                    </div>
                );
            case "MESSAGES":
                if (!messages) {
                    return null;
                }
                return (
                    <div className="contentView">
                        {
                            _.map(messages, (message, index) => {
                                let time = moment(moment.utc(message.createdAt)).local().calendar();
                                switch (message.type) {
                                    case "CONTACT":
                                        return (
                                            <MessageCellIncoming
                                                key={index}
                                                messageTimestamp={time}
                                                messageText={message.message}
                                            />
                                        )
                                    case "APP":
                                        return (
                                            <MessageCellBot
                                                key={index}
                                                messageTimestamp={time}
                                                messageText={message.message}
                                            />
                                        )
                                    default:
                                        return null;
                                }
                            })
                        }
                    </div>
                );
            default:
                return null;
        }
    }



    const handleCancelIgnore = () => {
        setOpenIgnoreModal(false);
        setAnswerIgnoreModal(null);
        setQuestionIgnoreModal(null);
        dispatch(updateExcludeComment(""));
    }

    const handleConfirmIgnore = (comment) => {
        updateAnswerIgnored(answerIgnoreModal, {ignored: true, ignoredComment: comment});
        setOpenIgnoreModal(false);
        setAnswerIgnoreModal(null);
        setQuestionIgnoreModal(null);
        dispatch(updateExcludeComment(""));
    }

    const ignoreAnswerModalContent = (
        <IgnoreAnswerModal
            answer={answerIgnoreModal}
            handleCancel={handleCancelIgnore}
            handleConfirm={handleConfirmIgnore}
        />
    );

    let content = open ? (
        <div className="modalDialog splitView surveyResultsView">
            <Modal
                content={ignoreAnswerModalContent }
                dialogClassName={"alertModal"}
                size="medium"
                open={openIgnoreModal}
            />
            <div className="mainView">
                <div className="navBar">
                    <h2 className="title">Details</h2>
                    <div className="separator"/>
                </div>
                {
                    surveyInstance ? (
                        <div className="scrollView">
                            <div className="contentView resultsDetails">
                                <SurveyResultsDetailsAttribute
                                    attributeTitle="Source"
                                    attributeDetail={getSurveyOriginationMethodValue(surveyInstance)}
                                />

                                <SurveyResultsDetailsAttribute
                                    attributeTitle="Contact"
                                    attributeDetail={surveyInstance.surveyInstance.contact.readableName}
                                />

                                {
                                    surveyInstance.originationMethod === "RULE_TRIGGER" && surveyInstance.ruleTriggerInstance ? (
                                        <SurveyResultsDetailsAttribute
                                            attributeTitle="Encounter"
                                            attributeDetail={(
                                                <EncounterDetails
                                                    encounterId={surveyInstance.ruleTriggerInstance.encounter.id}>
                                                    <a style={{cursor: 'pointer', textDecoration: 'underline', color: 'var(--colorTint)'}}
                                                       href={"#"}>#{surveyInstance.ruleTriggerInstance.encounter.sourceRefId}</a>
                                                </EncounterDetails>
                                            )}
                                        />
                                    ) : null
                                }

                                {
                                    surveyInstance.originationMethod === "RULE_TRIGGER" && surveyInstance.ruleTriggerInstance ? (
                                        <SurveyResultsDetailsAttribute
                                            attributeTitle="Incident Number"
                                            attributeDetail={surveyInstance.ruleTriggerInstance.encounter.incidentNumber ? "#" + surveyInstance.ruleTriggerInstance.encounter.incidentNumber : "N/A"}
                                        />
                                    ) : null
                                }

                                <SurveyTimestamps surveyInstance={surveyInstance} messages={messages}/>
                            </div>
                        </div>
                    ) : null
                }
            </div>

            <div className="detailView">
                <div className="navBar">
                    <h3 className="title">{surveyInstance ? surveyInstance.surveyInstance.surveyName : ""}</h3>
                    <div className={`segmentedBar index${_.indexOf(tabs, activeTab)}`} style={{width: "160px"}}
                         role="tablist">
                        {
                            _.map(tabs, function (t, idx) {
                                return (
                                    <div key={idx} onClick={() => setActiveTab(t)}
                                         className={`segment ${t === activeTab ? 'selected' : ''}`}>
                                        <div className="title" role="tab">{getTabTitle(t)}</div>
                                    </div>
                                );
                            })
                        }
                        <div className="selection" style={{width: (100 / tabs.length) + '%'}}/>
                    </div>
                    <div className="separator"/>
                </div>

                <div className="scrollView">
                    {getTabContents(activeTab)}
                </div>

                <div className="actionBar">
                    <div className="actions">
                        {isPrintSurveyEnabled() ?
                            <div onClick={() => window.print()} className="button medium tint">
                                <div className="title" role="button">Print</div>
                            </div> : null}

                        <AddTask props={props} taskTypes={taskTypes} onAdd={onAddTaskSubmit}>
                            <div className="button small action">
                                <div className="title" role="button">Add Task</div>
                            </div>
                        </AddTask>
                        {surveyInstance ?
                            (
                                surveyInstance.surveyInstance ? (
                                    surveyInstance.surveyInstance.review ?
                                        <div onClick={() => clearReviewFlag()}
                                             className="button small tint destructive">
                                            <div className="title" role="button">Clear Review Flag</div>
                                        </div> : null
                                ) : null
                            ) : null
                        }
                        {surveyInstance ?
                            (
                                surveyInstance.surveyInstance ? (
                                    !surveyInstance.surveyInstance.review && canQASurveys() ?
                                        <div onClick={() => addReviewFlag()} className="button small tint action">
                                            <div className="title" role="button">Mark For Review</div>
                                        </div> : null
                                ) : null
                            ) : null
                        }
                        <div onClick={() => closeModal()} className="button medium tint">
                            <div className="title" role="button">Done</div>
                        </div>
                    </div>

                    <div className="separator"/>
                </div>
            </div>
        </div>
    ) : null;

    const trigger = React.cloneElement(React.Children.only(props.children), {
        tabIndex: 0,
        role: 'button',
        onKeyDown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); }
        },
    });

    return (<Modal
        button={trigger}
        content={content}
        title={"Survey Results"}
        size="xxl"
        handleClose={() => closeModal()}
        handleOpen={() => setOpen(true)}
        open={open}
    />)
}

export default SurveyResults;